import { createRouter, createWebHistory } from 'vue-router'
import Login from '../views/Login.vue'
import Dashboard from '../views/Dashboard.vue'
import UserManagement from '../views/UserManagement.vue'
import UserProfile from '../views/UserProfile.vue'
import RolePermissions from '../views/RolePermissions.vue'
import RecordingAnalysis from '../views/RecordingAnalysis.vue'
import CourseManagement from '../views/CourseManagement.vue'
import EnrollmentApproval from '../views/EnrollmentApproval.vue'
import PaymentRecordReview from '../views/PaymentRecordReview.vue'
import StudentList from '../views/StudentList.vue'
import PotentialStudentList from '../views/PotentialStudentList.vue'
import StudentHistoryImport from '../views/StudentHistoryImport.vue'
import HistoryLog from '../views/HistoryLog.vue'
import BlacklistManagement from '../views/BlacklistManagement.vue'
import TagManagement from '../views/TagManagement.vue'
import TherapyDashboard from '../views/TherapyDashboard.vue'
import TherapistManagement from '../views/TherapistManagement.vue'
import TherapyActivityManagement from '../views/TherapyActivityManagement.vue'
import TherapyScheduleManagement from '../views/TherapyScheduleManagement.vue'
import TherapyBookingManagement from '../views/TherapyBookingManagement.vue'
import TherapyStoreManagement from '../views/TherapyStoreManagement.vue'
import TherapyReviewManagement from '../views/TherapyReviewManagement.vue'
import CustomerTypeManagement from '../views/CustomerTypeManagement.vue'
import TherapyCustomerManagement from '../views/TherapyCustomerManagement.vue'
import TherapyH5VirtualPhoneManagement from '../views/TherapyH5VirtualPhoneManagement.vue'
import TherapyBundleManagement from '../views/TherapyBundleManagement.vue'
import TherapyBundlePurchaseManagement from '../views/TherapyBundlePurchaseManagement.vue'
import FormManagement from '../views/FormManagement.vue'
import FormEditor from '../views/FormEditor.vue'
import FormSubmissions from '../views/FormSubmissions.vue'
import { authService } from '../services/authService'

const routes = [
  {
    path: '/',
    name: 'Login',
    component: Login
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    children: [
      {
        path: '',
        redirect: { name: 'Profile' }
      },
      {
        path: 'courses',
        name: 'CourseManagement',
        component: CourseManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'enrollments',
        name: 'EnrollmentApproval',
        component: EnrollmentApproval,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'payment-records',
        name: 'PaymentRecordReview',
        component: PaymentRecordReview,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'users',
        name: 'Users',
        component: UserManagement,
        beforeEnter: (to, from, next) => {
          // 检查用户角色，只有管理员可以访问用户管理页面
          if (authService.isAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'students',
        name: 'StudentList',
        component: StudentList,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'students-potential',
        name: 'PotentialStudentList',
        component: PotentialStudentList,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'students-import-history',
        name: 'StudentHistoryImport',
        component: StudentHistoryImport,
        beforeEnter: async (to, from, next) => {
          try {
            const { permissionService } = await import('@/services/permissionService')
            if (await permissionService.canAccessStudentHistoryImport()) {
              next()
            } else {
              next('/dashboard/profile')
            }
          } catch (e) {
            console.error(e)
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'profile',
        name: 'Profile',
        component: UserProfile
      },
      {
        path: 'role-permissions',
        name: 'RolePermissions',
        component: RolePermissions,
        beforeEnter: async (to, from, next) => {
          // 管理员可以访问角色权限管理页面
          if (authService.isAdmin()) {
            // 进一步检查权限配置
            try {
              const { permissionService } = await import('@/services/permissionService')
              const hasAccess = await permissionService.canAccessRolePermissions()
              if (hasAccess) {
                next()
              } else {
                console.log('权限不足，跳转到个人资料')
                next('/dashboard/profile')
              }
            } catch (error) {
              console.error('权限检查失败:', error)
              next('/dashboard/profile')
            }
          } else {
            console.log('权限不足，跳转到个人资料')
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'history',
        name: 'HistoryLog',
        component: HistoryLog,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'recording-analysis',
        name: 'RecordingAnalysis',
        component: RecordingAnalysis,
        beforeEnter: async (to, from, next) => {
          // 检查权限配置
          try {
            const { permissionService } = await import('@/services/permissionService')
            const hasAccess = await permissionService.canAccessRecordingAnalysis()
            if (hasAccess) {
              next()
            } else {
              console.log('权限不足，跳转到首个可访问页面')
              const firstPage = await permissionService.getFirstAccessiblePage()
              next(firstPage)
            }
          } catch (error) {
            console.error('权限检查失败:', error)
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'blacklist',
        name: 'BlacklistManagement',
        component: BlacklistManagement,
        beforeEnter: (to, from, next) => {
          // 检查用户角色，管理员和课程管理员都可以访问黑名单管理页面（公共资源）
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'tags',
        name: 'TagManagement',
        component: TagManagement,
        beforeEnter: (to, from, next) => {
          // 检查用户角色，管理员或课程管理员可以访问标签管理页面
          if (authService.isAdmin() || authService.isCourseAdmin()) {
            next()
          } else {
            next('/dashboard/profile')
          }
        }
      },
      {
        path: 'therapy-overview',
        name: 'TherapyOverview',
        component: TherapyDashboard,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapists',
        name: 'TherapistManagement',
        component: TherapistManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-activities',
        name: 'TherapyActivityManagement',
        component: TherapyActivityManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-schedules',
        name: 'TherapyScheduleManagement',
        component: TherapyScheduleManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-bookings',
        name: 'TherapyBookingManagement',
        component: TherapyBookingManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-store',
        name: 'TherapyStoreManagement',
        component: TherapyStoreManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-reviews',
        name: 'TherapyReviewManagement',
        component: TherapyReviewManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'customer-types',
        name: 'CustomerTypeManagement',
        component: CustomerTypeManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-bundles',
        name: 'TherapyBundleManagement',
        component: TherapyBundleManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-bundle-purchases',
        name: 'TherapyBundlePurchaseManagement',
        component: TherapyBundlePurchaseManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-customers',
        name: 'TherapyCustomerManagement',
        component: TherapyCustomerManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'therapy-h5-virtual-phones',
        name: 'TherapyH5VirtualPhoneManagement',
        component: TherapyH5VirtualPhoneManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin() || authService.isTherapyAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'forms',
        name: 'FormManagement',
        component: FormManagement,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'forms/:id/edit',
        name: 'FormEditor',
        component: FormEditor,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin()) next()
          else next('/dashboard/profile')
        }
      },
      {
        path: 'forms/:id/submissions',
        name: 'FormSubmissions',
        component: FormSubmissions,
        beforeEnter: (to, from, next) => {
          if (authService.isAdmin()) next()
          else next('/dashboard/profile')
        }
      }
    ],
    beforeEnter: (to, from, next) => {
      if (authService.isAuthenticated()) {
        next();
      } else {
        next('/');
      }
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 全局路由守卫
router.beforeEach(async (to, from, next) => {
  const isAuthenticated = authService.isAuthenticated()
  
  // 如果访问登录页面且已登录，重定向到首个可访问页面
  if (to.name === 'Login' && isAuthenticated) {
    try {
      const { permissionService } = await import('@/services/permissionService')
      const firstPage = await permissionService.getFirstAccessiblePage()
      next(firstPage)
    } catch (error) {
      console.error('获取首个可访问页面失败:', error)
      next('/dashboard/profile')
    }
  }
  // 如果访问需要认证的页面但未登录，重定向到登录页
  else if (to.name !== 'Login' && !isAuthenticated) {
    next('/')
  }
  else {
    next()
  }
})

export default router